import React, { useState, useEffect, useMemo, useCallback } from 'react';
import styled, { css, keyframes } from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { useRecoilValue } from 'recoil';
import { currentUserIDState } from '@atom/setting/user';
import { scrollbarHide } from '@styles/variants/scrollbar';
import Icon from '@components/dataDisplay/Icons/Icon';
import { IQuest } from '@typings/quest/questTypes';
import { mainHomeSettingState } from '@atom/setting/homeSettingState';
import { LATEST_DOCS_TYPE } from '@typings/mainHome/latestDocs';
import { useCollection, useDocument } from '@nandorojo/swr-firestore';
import { CollectionPath } from '@typings/collections.enum';
import {
  IMyBettingBet,
  IBettingItem,
} from '@typings/betting/betting.interface';
import {
  IattendanceItem,
  IattendanceRewardSetting,
  IStreakReward,
  STREAK_REWARD_TYPE_LABELS,
} from '@typings/calculate/attendance.interface';
import { IPhotoCard } from '@typings/photocard/photocard.interface';
import { ExplorationArea } from '@typings/worldmap/explorationArea.interface';
import {
  calculateRemainingTime,
  formatRemainingTime,
} from '@store/firebase/dispatch/dispatch';
import Avatar from '@components/dataDisplay/Avatar';
import DispatchDetailModal from '@pages/WorldMap/Panel/DispatchDetailModal';
import dayjs from 'dayjs';

// 연속 출석 보상 수령 기록 인터페이스
interface IStreakClaim {
  id?: string;
  authorUid: string;
  streakRewardId: string;
  requiredDays: number;
  claimedAt: Date;
}

// ── 파견 항목 (컴포넌트 바깥 정의 → 안정적 타입 참조, 리마운트 방지) ──
const DispatchRow = React.memo(function DispatchRow({
  character,
  onClick,
  delay,
}: {
  character: IPhotoCard;
  onClick: () => void;
  delay: number;
}) {
  const [remainingTime, setRemainingTime] = useState<string>('');
  const [isCompleted, setIsCompleted] = useState(false);

  // 파견 지역 정보 가져오기
  const { data: areaData } = useDocument<ExplorationArea>(
    character.dispatchAreaId
      ? `${CollectionPath.WORLDMAP}/${character.dispatchAreaId}`
      : null,
  );

  // 타이머 업데이트
  useEffect(() => {
    if (!character.dispatchStartedAt || !character.dispatchDuration) return;

    const updateTimer = () => {
      const remaining = calculateRemainingTime(
        character.dispatchStartedAt!,
        character.dispatchDuration!,
      );
      if (remaining <= 0) {
        setRemainingTime('완료!');
        setIsCompleted(true);
      } else {
        setRemainingTime(formatRemainingTime(remaining));
        setIsCompleted(false);
      }
    };

    updateTimer();
    const interval = setInterval(updateTimer, 1000);

    return () => clearInterval(interval);
  }, [character.dispatchStartedAt, character.dispatchDuration]);

  const areaName = areaData?.name || '파견 중...';

  return (
    <DispatchItem
      isCompleted={isCompleted}
      onClick={onClick}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay }}
    >
      <Avatar photoURL={character.thumbnail || ''} size="32px" />
      <DispatchCharInfo>
        <DispatchCharName>{character.title}</DispatchCharName>
        <DispatchAreaName>📍 {areaName}</DispatchAreaName>
      </DispatchCharInfo>
      <DispatchTimer isCompleted={isCompleted}>
        {isCompleted ? '🎁 수령하기' : `⏱️ ${remainingTime}`}
      </DispatchTimer>
    </DispatchItem>
  );
});

// ── 베팅 항목 (컴포넌트 바깥 정의 → 안정적 타입 참조, 리마운트 방지) ──
const MyBettingRow = React.memo(function MyBettingRow({
  bet,
  onBettingClick,
}: {
  bet: IMyBettingBet;
  onBettingClick: (topicId: string) => void;
}) {
  const { data: topic } = useDocument<IBettingItem>(
    bet?.topicId ? `${CollectionPath.BETTING}/${bet.topicId}` : null,
    { listen: true },
  );

  // 베팅 주제가 숨김 상태이면 표시하지 않음
  if (topic?.status === 'hidden') {
    return null;
  }

  const title = topic?.title || '베팅';
  const createdText = bet?.createdAt
    ? new Date(bet.createdAt as any).toLocaleString()
    : '-';
  const now = Date.now();
  const closed = !!topic?.announceAt && now >= Number(topic.announceAt);
  const statusText = closed ? '[마감]' : '[진행중]';
  return (
    <QuestItem
      onClick={() => onBettingClick(String(bet.topicId || ''))}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2 }}
    >
      <IconCell>
        <Icon name={LATEST_DOCS_TYPE.QUEST} color="var(--menuTextColor)" />
      </IconCell>
      <QuestItemTitle>
        {title}{' '}
        <span style={{ color: 'var(--subTextColor)', fontWeight: 500 }}>
          {statusText}
        </span>
      </QuestItemTitle>
      <QuestItemDesc>
        <div>
          <Summary>참여 일시: {createdText}</Summary>
        </div>
      </QuestItemDesc>
    </QuestItem>
  );
});

function MiniQuest({ padding }: { padding?: number }) {
  const [quests, setQuests] = useState<IQuest[]>([]);
  const [loading, setLoading] = useState(false);
  const mainHomeData = useRecoilValue(mainHomeSettingState);
  const currentUserId = useRecoilValue(currentUserIDState);

  // 파견 상세 모달 상태
  const [selectedDispatchChar, setSelectedDispatchChar] = useState<IPhotoCard | null>(null);
  const [isDispatchModalOpen, setIsDispatchModalOpen] = useState(false);

  // users/{uid}/quests, users/{uid}/bettings 실시간 구독
  const { data: userQuests } = useCollection<IQuest>(
    currentUserId ? `${CollectionPath.USERS}/${currentUserId}/quests` : null,
    { listen: true, orderBy: ['createdAt', 'desc'] },
  );
  const { data: myBettings } = useCollection<IMyBettingBet>(
    currentUserId ? `${CollectionPath.USERS}/${currentUserId}/bettings` : null,
    { listen: true, orderBy: ['createdAt', 'desc'] },
  );

  // 출석 보상 설정 가져오기
  const { data: rewardSetting } = useDocument<IattendanceRewardSetting>(
    `${CollectionPath.EVENT}/attendanceReward`,
    { listen: true },
  );

  // 출석 목록 가져오기
  const { data: attendanceList } = useCollection<IattendanceItem>(
    `${CollectionPath.CALCULATE}/attendance/records`,
    {
      listen: true,
      orderBy: ['createdAt', 'desc'],
      limit: 100,
    },
  );

  // 연속 출석 보상 수령 기록 가져오기
  const { data: streakClaims } = useCollection<IStreakClaim>(
    currentUserId
      ? `${CollectionPath.CALCULATE}/attendance/streakClaims`
      : null,
    {
      where: currentUserId ? ['authorUid', '==', currentUserId] : undefined,
      listen: true,
    },
  );

  // 파견 중인 내 캐릭터 목록 가져오기
  const { data: dispatchedCharacters, mutate: mutateDispatched } = useCollection<IPhotoCard>(
    currentUserId ? CollectionPath.PHOTOCARD : null,
    {
      where: [
        ['owner', '==', currentUserId || ''],
        ['isDispatched', '==', true],
      ],
      listen: true,
    },
  );

  // 오늘 날짜
  const today = useMemo(() => dayjs().format('YYYY-MM-DD'), []);

  // 연속 출석 일수 계산
  const currentStreak = useMemo(() => {
    if (!attendanceList || !currentUserId) return 0;

    const myAttendanceList = attendanceList.filter(
      (item) => item.authorUid === currentUserId,
    );

    if (!myAttendanceList.length) return 0;

    const uniqueDates = Array.from(
      new Set(myAttendanceList.map((item) => item.date)),
    );
    const sortedDates = uniqueDates.sort().reverse();

    let streak = 0;
    let checkDate = dayjs(today);

    if (!sortedDates.includes(today)) {
      checkDate = checkDate.subtract(1, 'day');
    }

    for (let i = 0; i < sortedDates.length; i++) {
      const expectedDate = checkDate.subtract(i, 'day').format('YYYY-MM-DD');
      if (sortedDates.includes(expectedDate)) {
        streak++;
      } else {
        break;
      }
    }

    return streak;
  }, [attendanceList, currentUserId, today]);

  // 이미 수령한 보상 ID 목록
  const claimedRewardIds = useMemo(
    () => streakClaims?.map((claim) => claim.streakRewardId) || [],
    [streakClaims],
  );

  // 활성화된 연속 출석 보상 목록 (정렬)
  const activeStreakRewards = useMemo(() => {
    return (
      rewardSetting?.streakRewards
        ?.filter((r) => r.isActive)
        ?.sort((a, b) => a.requiredDays - b.requiredDays) || []
    );
  }, [rewardSetting?.streakRewards]);

  // 출석 페이지로 이동
  const handleAttendanceClick = useCallback(() => {
    // 정산 페이지의 출석 섹션으로 이동
    window.location.href = '/calculate?tab=attendance';
  }, []);

  // 파견 캐릭터 클릭 핸들러
  const handleDispatchClick = useCallback((character: IPhotoCard) => {
    setSelectedDispatchChar(character);
    setIsDispatchModalOpen(true);
  }, []);

  // 파견 모달 닫기
  const handleDispatchModalClose = useCallback(() => {
    setIsDispatchModalOpen(false);
    setSelectedDispatchChar(null);
  }, []);

  // 파견 완료 후 목록 새로고침
  const handleDispatchComplete = useCallback(() => {
    mutateDispatched();
  }, [mutateDispatched]);

  // 퀘스트 클릭 핸들러 → 퀘스트 페이지로 이동 (해당 퀘스트 선택된 상태로)
  const handleQuestClick = (questId: string) => {
    window.location.href = `/quest?id=${questId}`;
  };

  // 베팅 클릭 핸들러 → 베팅 페이지로 이동
  const handleBettingClick = (bettingId: string) => {
    window.location.href = `/betting?id=${bettingId}`;
  };

  // 로그인하지 않은 사용자는 렌더링하지 않음
  if (!currentUserId) {
    return null;
  }

  return (
    <>
      <MiniQuestContainer
        initial={{ opacity: 0, x: 100 }}
        animate={{ opacity: 1, x: 0 }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
        padding={padding}
      >
        <AnimatePresence>
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3 }}
          >
            <QuestList>
              {loading ? (
                <EmptyState>
                  <EmptyIcon>⏳</EmptyIcon>
                  <div>로딩 중...</div>
                </EmptyState>
              ) : null}

              {/* 내 퀘스트 목록 (0개면 섹션 미표시) */}
              {userQuests && userQuests.length > 0 && (
                <>
                  <SubTitle style={{ padding: '6px 8px' }}>퀘스트</SubTitle>
                  {userQuests.map((quest, index) => (
                    <QuestItem
                      key={quest.id as any}
                      onClick={() => handleQuestClick(String(quest.id))}
                      initial={{ opacity: 0, y: 20 }}
                      animate={{ opacity: 1, y: 0 }}
                      transition={{ delay: index * 0.05 }}
                    >
                      <IconCell>
                        <Icon
                          name={LATEST_DOCS_TYPE.QUEST}
                          color="var(--menuTextColor)"
                        />
                      </IconCell>
                      <QuestItemTitle>
                        {(quest as any).title || '퀘스트'}
                      </QuestItemTitle>
                      <QuestItemDesc>
                        <div>
                          {(quest as any).subTitle && (
                            <SubTitle>{(quest as any).subTitle}</SubTitle>
                          )}
                          {(quest as any).summary && (
                            <Summary>{(quest as any).summary}</Summary>
                          )}
                        </div>
                      </QuestItemDesc>
                    </QuestItem>
                  ))}
                </>
              )}

              {/* 내 베팅 목록 (0개면 섹션 미표시) */}
              {myBettings && myBettings.length > 0 && (
                <>
                  <SubTitle style={{ padding: '6px 8px' }}>베팅</SubTitle>
                  {myBettings.map((bet, index) => (
                    <MyBettingRow
                      key={(bet as any).id || `${bet.topicId}__${bet.userId}`}
                      bet={bet}
                      onBettingClick={handleBettingClick}
                    />
                  ))}
                </>
              )}

              {/* 파견 중인 캐릭터 목록 */}
              {dispatchedCharacters && dispatchedCharacters.length > 0 && (
                <>
                  <SubTitle style={{ padding: '6px 8px' }}>
                    🚀 파견 중 ({dispatchedCharacters.length}명)
                  </SubTitle>
                  {dispatchedCharacters.map((character, index) => (
                    <DispatchRow
                      key={character.id}
                      character={character}
                      onClick={() => handleDispatchClick(character)}
                      delay={index * 0.05}
                    />
                  ))}
                </>
              )}

              {/* 연속 출석 보상 (활성화된 항목이 있을 때만 표시) */}
              {activeStreakRewards.length > 0 && (
                <>
                  <SubTitle style={{ padding: '6px 8px' }}>
                    🔥 연속 출석 보상{' '}
                    <span style={{ color: 'var(--primaryColor)', fontWeight: 'bold' }}>
                      (+{currentStreak}일)
                    </span>
                  </SubTitle>
                  {activeStreakRewards.map((reward, index) => {
                    const isClaimed = claimedRewardIds.includes(reward.id);
                    const isClaimable = !isClaimed && currentStreak >= reward.requiredDays;
                    const remainingDays = reward.requiredDays - currentStreak;

                    return (
                      <StreakRewardItem
                        key={reward.id}
                        isClaimable={isClaimable}
                        isClaimed={isClaimed}
                        onClick={handleAttendanceClick}
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: index * 0.05 }}
                      >
                        <StreakDays isClaimable={isClaimable}>
                          {reward.requiredDays}일
                        </StreakDays>
                        <StreakRewardInfo>
                          <StreakRewardTitle>
                            {reward.rewardType === 'currency' &&
                              `${reward.rewardAmount?.toLocaleString() || 0}원`}
                            {reward.rewardType === 'item' && '아이템 보상'}
                            {reward.rewardType === 'title' && '타이틀 보상'}
                          </StreakRewardTitle>
                          <StreakRewardType>
                            {STREAK_REWARD_TYPE_LABELS[reward.rewardType]}
                          </StreakRewardType>
                        </StreakRewardInfo>
                        <StreakStatus isClaimable={isClaimable}>
                          {isClaimed
                            ? '수령완료 ✓'
                            : isClaimable
                              ? '수령 가능!'
                              : `${remainingDays}일 남음`}
                        </StreakStatus>
                      </StreakRewardItem>
                    );
                  })}
                </>
              )}
            </QuestList>
          </motion.div>
        </AnimatePresence>
      </MiniQuestContainer>

      {/* 파견 상세 모달 */}
      <DispatchDetailModal
        isOpen={isDispatchModalOpen}
        character={selectedDispatchChar}
        userId={currentUserId || ''}
        onClose={handleDispatchModalClose}
        onComplete={handleDispatchComplete}
      />
    </>
  );
}

export default MiniQuest;



// 메인 컨테이너
const MiniQuestContainer = styled(motion.div) <{ padding?: number }>`
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
`;

// 퀘스트 리스트
const QuestList = styled.div`
  max-height: 600px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;
  ${scrollbarHide}
`;

const CommonQuestItemStyle = css`
  background: linear-gradient(90deg, var(--cardBackgroundColor), rgba(0,0,0,0));
  border-left: 4px solid var(--lineColor);
  border-radius: var(--cardBorderRadius);
  cursor: var(--hoverCursorURL), pointer;
  overflow: hidden;
  transition: all 0.25s ease;
line-height: 1.2;
overflow: hidden;
  padding: 10px 16px;

  &:hover {
    background: linear-gradient(90deg, var(--cardBackgroundColor), var(--cardBackgroundColor), rgba(0,0,0,0));
  }
`;

// 퀘스트 아이템
const QuestItem = styled(motion.div)`
  display: grid;
  grid-template-columns: auto 1fr;
  grid-template-rows: auto auto;
  grid-template-areas:
    'icon title'
    'desc desc';
  align-items: center;
  column-gap: 8px;
  row-gap: 0px;

  ${CommonQuestItemStyle}
`;

const IconCell = styled.div`
  grid-area: icon;
  display: flex;
  align-items: center;
`;

const QuestItemTitle = styled.div`
  grid-area: title;
  font-size: 14px;
  font-weight: 600;
  color: var(--textColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const QuestItemDesc = styled.div`
  grid-area: desc;
  display: grid;
  grid-template-rows: 0fr; /* collapsed */
  transition: grid-template-rows 0.28s ease;

  ${QuestItem}:hover & {
    grid-template-rows: 1fr; /* expand */
  }

  & > div {
    overflow: hidden;
  }
`;

const SubTitle = styled.div`
  color: var(--subTextColor);
  font-size: 12px;
  opacity: 0.9;
`;

const Summary = styled.div`
  color: var(--subTextColor);
  font-size: 12px;
  opacity: 0.9;
`;
// 빈 상태
const EmptyState = styled.div`
  padding: 24px 16px;
  text-align: center;
  color: var(--subTextColor);
  font-size: 0.85rem;
`;

const EmptyIcon = styled.div`
  font-size: 2rem;
  margin-bottom: 8px;
  opacity: 0.5;
`;

// 연속 출석 보상 아이템
const StreakRewardItem = styled(motion.div) <{ isClaimable: boolean; isClaimed: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;
  transition: all 0.25s ease;

  ${CommonQuestItemStyle}
`;

const StreakDays = styled.div<{ isClaimable: boolean }>`
  font-size: 14px;
  font-weight: bold;
  color: ${({ isClaimable }) => (isClaimable ? 'var(--primaryColor)' : 'var(--subTextColor)')};
  min-width: 50px;
`;

const StreakRewardInfo = styled.div`
  display: flex;
  gap: 6px;
`;

const StreakRewardTitle = styled.div`
  font-size: 13px;
  color: var(--textColor);
`;

const StreakRewardType = styled.span`
  font-size: 11px;
  color: var(--subTextColor);
  padding: 2px 0;
  background: var(--hoverColor);
  border-radius: 4px;
`;

const StreakStatus = styled.div<{ isClaimable: boolean }>`
  font-size: 11px;
  color: ${({ isClaimable }) => (isClaimable ? 'var(--primaryColor)' : 'var(--subTextColor)')};
  font-weight: ${({ isClaimable }) => (isClaimable ? '600' : '400')};
`;
// 파견 아이템 스타일
const DispatchItem = styled(motion.div) <{ isCompleted: boolean }>`
  display: grid;
  grid-template-columns: auto 1fr auto;
  align-items: center;
  gap: 12px;
  padding: 10px 16px;

  border-left: 4px solid ${({ isCompleted }) =>
    isCompleted ? 'var(--primaryColor)' : 'var(--lineColor)'};
  ${CommonQuestItemStyle}
`;

const DispatchCharInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const DispatchCharName = styled.div`
  font-size: 13px;
  font-weight: 600;
  color: var(--textColor);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const DispatchAreaName = styled.div`
  font-size: 11px;
  color: var(--subTextColor);
`;

const DispatchTimer = styled.div<{ isCompleted: boolean }>`
  font-size: 12px;
  font-weight: ${({ isCompleted }) => (isCompleted ? '600' : '500')};
  color: ${({ isCompleted }) => (isCompleted ? 'var(--primaryColor)' : 'var(--subTextColor)')};
  white-space: nowrap;
`;

